import { z } from 'zod';
import { useEffect, useMemo, useRef } from 'react';
import { definePageSkill, useRegisterPageSkill } from '@ev-tech/search-modules-chatbot';
import { supportedContactWizardTenants } from '@ev-tech/lead-flows/contact-wizard/supported-tenants';
import { ContactIntent, ContactWizardStep, useContactWizard } from '@ev-tech/lead-flows';

import { useSettings } from '@/hooks/useSettings';

const wizardStartSchema = z.object({
  initialStep: z
    .enum([
      ContactWizardStep.ContactIntentSelection,
      ContactWizardStep.SearchIntentForm,
      ContactWizardStep.SellIntentForm,
      ContactWizardStep.CareerIntentForm,
      ContactWizardStep.OtherIntentForm,
    ])
    .optional()
    .catch(undefined),
  initialIntent: z.enum(ContactIntent).optional().catch(undefined),
});

export const ChatbotContactWizard = () => {
  const { open } = useContactWizard();
  const { countryCode } = useSettings();
  const available = supportedContactWizardTenants.some((tenant) => tenant === countryCode.toLowerCase());

  // `open` changes on every render because it's not memoized; the ref keeps the skill stable so pending confirmations don't expire.
  const openRef = useRef(open);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  const skill = useMemo(
    () =>
      definePageSkill({
        type: 'open-contact-wizard',
        description:
          'Only when you cannot answer the question or fulfill the request, offer to contact our team. In your reply, ask whether the user would like you to open the contact form for their request.',
        confirmation: {
          confirmLabelKey: 'chatbot.openContactWizard',
          previewField: 'message',
          closeChat: true,
        },
        responseSchema: {
          schemaDefinition: {
            message: 'The user’s question or request to prefill in the contact form. Preserve their intent and language.',
            initialStep:
              'Choose the most suitable starting step: SearchIntentForm, SellIntentForm, CareerIntentForm, OtherIntentForm, or ContactIntentSelection when unclear. Match the initialIntent.',
            //TODO: Use after description length limit has been increased: 'Choose the most suitable starting step: SearchIntentForm for buying or renting, SellIntentForm for selling, letting or valuation, CareerIntentForm for careers, OtherIntentForm for other enquiries, or ContactIntentSelection when unclear. Match the initialIntent. Do not skip to later steps.',
            initialIntent:
              'Choose the matching contact intent: SEARCH for buying or renting, OWNER for selling, letting or valuation, CAREER for careers, or OTHER for other enquiries. Omit when unclear.',
          },
          mandatoryFields: ['message'],
        },
        execute: ({ message, initialStep, initialIntent }) => {
          openRef.current({
            ...wizardStartSchema.parse({ initialStep, initialIntent }),
            prefillInitialMessage: { sell: message, buy: message, work: message, other: message },
          });
        },
      }),
    [],
  );

  useRegisterPageSkill(available ? skill : null);

  return null;
};
